/**
 * フィールドキーからFormFieldWrapperが生成するID形式に変換
 *
 * @param fieldKey - フィールドキー（例: 'address.city', 'items[0]'）
 * @returns fieldId形式（例: 'field-address-city', 'field-items-0-'）
 */
export const toFieldId = (fieldKey: string): string => {
  return `field-${fieldKey.replace(/[.[\]]/g, '-')}`;
};

/**
 * 要素が表示されているかどうかを判定
 *
 * @param element - 判定対象の要素
 * @returns 表示されている場合はtrue
 */
const isVisible = (element: HTMLElement): boolean => {
  return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
};

/**
 * 指定されたフィールドキーに対応するDOM要素を検索
 *
 * 以下の優先順位で要素を検索:
 * 1. name属性（Input, Textarea など）
 * 2. data-field属性（カスタムコンポーネント）
 * 3. id属性（FormFieldWrapper が生成する field-xxx 形式）
 *
 * 非表示要素（hidden input等）はスキップされます。
 *
 * @param fieldKey - フィールドキー
 * @returns 見つかった要素、見つからない場合はnull
 */
export const findFieldElement = (fieldKey: string): HTMLElement | null => {
  const fieldId = toFieldId(fieldKey);

  // 検索セレクタの優先順位リスト
  const selectors = [`[name="${fieldKey}"]`, `[data-field="${fieldKey}"]`, `#${fieldId}`];

  for (const selector of selectors) {
    const elements = document.querySelectorAll<HTMLElement>(selector);
    for (const element of elements) {
      // 表示されている要素のみを返す
      if (isVisible(element)) {
        return element;
      }
    }
  }

  return null;
};

/**
 * 指定されたフィールドにスクロールしてフォーカスを当てる
 *
 * 以下の優先順位で要素を検索:
 * 1. name属性（Input, Textarea など）
 * 2. data-field属性（カスタムコンポーネント）
 * 3. id属性（FormFieldWrapper が生成する field-xxx 形式）
 *
 * @param fieldKey - フィールドキー
 *
 * @example
 * ```ts
 * // Input フィールドにスクロール
 * scrollToField('email');
 *
 * // ネストされたフィールドにスクロール
 * scrollToField('address.city');
 *
 * // Select/Cascader フィールドにスクロール（id属性で検索）
 * scrollToField('category_id');
 * ```
 */
export const scrollToField = (fieldKey: string): void => {
  const element = findFieldElement(fieldKey);

  if (element) {
    // スムーズにスクロール
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // 少し遅延させてフォーカス（スクロール完了後）
    setTimeout(() => {
      element.focus();
    }, 300);
  }
};
